// src/encounter/services/use-save-allergy.ts
// Rail-scoped save hook for the IPS Allergies section. Wraps the curl-verified Allergy
// endpoints (POST /api/Patients/CreateAllergy, PUT /api/Patients/allergy/{id}) and, on
// success, invalidates the ENCOUNTER summary query (['patient-summary', patientId]) so the
// pre-encounter rail reflects the change. This intentionally differs from the patients-module
// useCreateAllergy/useUpdateAllergy hooks, which invalidate the chart key
// (['patient-allergies', patientId]) — the rail reads from GetPatientSummary, a different query.
//
// Mirrors use-save-flags: one hook, takes the already-built request (from buildAllergyRequest),
// dispatches create vs update by `kind`. Body assembly + wire-shape rules live in
// lib/to-allergy-request.ts; this hook owns only the transport + cache invalidation.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { AllergyRequest } from '@/encounter/lib/to-allergy-request';

export function useSaveAllergy(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, AllergyRequest>({
    mutationFn: (req: AllergyRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateAllergy', req.body);
      }
      // update path covers EDIT and soft-DELETE (only body.isDeleted differs).
      return put<string>(`/api/Patients/allergy/${req.allergyId}`, req.body);
    },
    onSuccess: () => {
      // The rail RENDERS from the summary projection but sources edit/delete round-trips from the
      // full chart list (usePatientAllergies, ['patient-allergies', patientId]). Invalidate BOTH so
      // a freshly-added row also gets its full record (with recorder) — otherwise its edit/delete
      // affordance stays suppressed / no-ops (Codex MAJ-2).
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-allergies', patientId] });
    },
  });
}
